import dynamic from 'next/dynamic';
import type { HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';

import { useScrollLock, useWindowWidth } from '@virginexperiencedays/hooks';
import { useMediaLightBox } from '@virginexperiencedays/components-v2/src/layout/MediaLightBox/hooks/useMediaLightBox';
import { transformToLargeDir } from '@virginexperiencedays/components-v2/src/pages/product/HeroImages/transformToLargeDir';
import { MediaGrid } from '../MediaGrid';
import { mediaGridExtractor } from '../VariationPage/extractors';

import { DataTestId } from '../VariationPage/PageUI';

import type { Product, Video, Image as ImageType } from '@virginexperiencedays/products';
import type { CarouselCallbackProps } from '@virginexperiencedays/components-v2/src/carousels/EmblaCarousel';
import type { AddToBasketWidgetProps } from '../Widgets/AddToBasketWidget';

const MediaLightBox = dynamic(() => import('../MediaLightBox'), { ssr: false });

type Props = {
  product: Product;
  addToBasket: Omit<AddToBasketWidgetProps, 'onClick'> & { onClick: () => Promise<boolean> };
  hideAddToBasket?: boolean;
  lightBoxClassName?: string;
  onClickImage?: ({ currentIndex, totalSlides }) => void;
  onClickNav?: (data: CarouselCallbackProps) => void;
  onSwipeNav?: (data: CarouselCallbackProps) => void;
} & HTMLAttributes<HTMLDivElement>;

export const MediaGridWidget = ({
  className,
  lightBoxClassName,
  product,
  addToBasket,
  hideAddToBasket,
  onClickImage,
  onClickNav,
  onSwipeNav,
}: Props) => {
  const { media } = product;

  const { isDesktop } = useWindowWidth();
  const imageLayout = !isDesktop() ? { width: 960, height: 644 } : { fill: true };

  const {
    handleOpen,
    isOpen,
    handleOnNavClick: baseHandleOnNavClick,
    handleOnSwipe: baseHandleOnSwipe,
    ...lightBoxProps
  } = useMediaLightBox<ImageType | Video>(
    [media.video, media.mainImage, ...media.images].filter(Boolean),
    (media) =>
      media.map((mediaItem) => ({
        ...mediaItem,
        url: mediaItem && 'url' in mediaItem ? transformToLargeDir(mediaItem.url) : undefined,
        fallbackUrl: mediaItem && 'url' in mediaItem ? mediaItem.url : undefined,
      }))
  );

  useScrollLock(isOpen);

  const mediaGridProps = mediaGridExtractor(media);

  const handleOnNavClick = (data: CarouselCallbackProps) => {
    baseHandleOnNavClick?.(data);
    onClickNav?.(data);
  };

  const handleOnSwipe = (data: CarouselCallbackProps) => {
    onSwipeNav?.(data);
  };

  const handleOnSwipeRelease = (data: CarouselCallbackProps) => {
    baseHandleOnSwipe?.(data);
  };

  return (
    <>
      <MediaGrid
        className={className}
        isLightBoxOpen={isOpen}
        {...mediaGridProps}
        handleOpen={(i) => {
          handleOpen(i);
          onClickImage?.({
            currentIndex: i,
            totalSlides: mediaGridProps.mediaCount,
          });
        }}
      />
      {isOpen &&
        createPortal(
          <MediaLightBox
            className={lightBoxClassName}
            imageLayout={imageLayout}
            data-testid={DataTestId.MediaLightBox}
            productTitle={product.title}
            addToBasket={!hideAddToBasket ? addToBasket : undefined}
            handleOnNavClick={handleOnNavClick}
            handleOnSwipe={handleOnSwipe}
            handleOnSwipeRelease={handleOnSwipeRelease}
            videoUrl={process.env.NEXT_PUBLIC_BUNNY_STREAM_CDN_BASE_URL}
            videoVolume={parseFloat(process.env.NEXT_PUBLIC_PDP_VIDEO_VOLUME || '1')}
            {...lightBoxProps}
          />,
          document.body
        )}
    </>
  );
};
